import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Is this element's text actually being cut off right now?
 *
 * The point is to attach a tooltip ONLY when there is something hidden to reveal. A tooltip that
 * repeats text already fully on screen is noise, and it fires on every row of a list; a tooltip that
 * never appears leaves the truncated row unreadable. Neither can be decided from the string length —
 * it depends on the rendered width, the font, and the clamp, so it has to be measured.
 *
 * Handles both directions: `line-clamp-*` overflows vertically (scrollHeight), `truncate` overflows
 * horizontally (scrollWidth).
 *
 * ⚠️ Re-measures on RESIZE, not just on mount. The shell's panels push `main`, so a row's width
 * changes when a panel opens with no re-render of the row and no change to its text — measured once
 * at mount, a row would keep a tooltip it no longer needs, or lose one it now does.
 *
 * ⚠️ The +1 tolerances are not superstition: sub-pixel layout makes scrollHeight exceed clientHeight
 * by a fraction on plenty of untruncated elements, which would mark half a list as truncated.
 */
export function useIsTruncated<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setIsTruncated(el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    measure();

    // ResizeObserver is not available in the vitest (node, no jsdom) environment, and this hook is
    // reached by any component test that ever gets written. Degrade to the mount measurement.
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  return { ref, isTruncated, measure };
}
